import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEditableTarget } from './isEditableTarget';
import { IS_MAC, matchesBinding, parseBinding } from './parseBinding';
import { SHELL_SHORTCUTS, ShortcutDefinition } from './registry';

/**
 * Returns true if the keyboard event is a browser-reserved shortcut that
 * should never be intercepted (e.g. DevTools, page refresh, view source).
 */
function isBrowserReservedShortcut(event: KeyboardEvent): boolean {
  // KeyboardEvent.key is spec'd as a string but real-world events can
  // omit it — IME composition, browser-extension synthetic events,
  // some dead-key sequences. Bail out as "not a reserved shortcut"
  // when the key is missing rather than crashing the global handler.
  if (typeof event.key !== 'string') return false;
  const key = event.key.toLowerCase();
  const code = event.code;

  if (IS_MAC) {
    // ⌘+⌥+I  (DevTools / Elements)
    // ⌘+⌥+J  (DevTools / Console)
    // ⌘+⌥+C  (Inspect element)
    if (event.metaKey && event.altKey && !event.ctrlKey && !event.shiftKey) {
      if (key === 'i' || key === 'j' || key === 'c' ||
          code === 'KeyI' || code === 'KeyJ' || code === 'KeyC') {
        return true;
      }
    }
  } else {
    // Ctrl+Shift+I  (DevTools / Elements)
    // Ctrl+Shift+J  (DevTools / Console)
    // Ctrl+Shift+C  (Inspect element)
    if (event.ctrlKey && event.shiftKey && !event.metaKey && !event.altKey) {
      if (key === 'i' || key === 'j' || key === 'c' ||
          code === 'KeyI' || code === 'KeyJ' || code === 'KeyC') {
        return true;
      }
    }
    // F12 (DevTools)
    if (key === 'f12' || code === 'F12') return true;
  }

  return false;
}

/**
 * True when the focused element natively handles Enter / Space itself
 * (buttons, links, and their ARIA equivalents). A global *unmodified*
 * binding on those keys must not steal them, or keyboard activation of
 * ordinary controls stops working while the binding's page is mounted.
 */
function ownsActivationKeys(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'BUTTON' || tag === 'SUMMARY') return true;
  if (tag === 'A' && target.hasAttribute('href')) return true;
  const role = target.getAttribute('role');
  return role === 'button' || role === 'link' || role === 'menuitem'
    || role === 'menuitemcheckbox' || role === 'menuitemradio'
    || role === 'tab' || role === 'option';
}

/**
 * True while a modal dialog is mounted. Every modal in the suite renders
 * through `BaseModal`, which portals `[aria-modal="true"]` only while open
 * and puts initial focus on its own panel — so a page shortcut would
 * otherwise fire on the page *behind* the dialog the operator is reading
 * (a stray Enter navigating away is the sharp case).
 */
function isModalOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector('[aria-modal="true"]') !== null;
}

type Handler = (event: KeyboardEvent) => void;

type Registration = {
  id: string;
  handler: Handler;
  scope?: string;
};

type ShortcutContextValue = {
  register: (id: string, handler: Handler) => () => void;
  pushScope: (scope: string) => () => void;
  /** Returns a snapshot of the currently active scopes. */
  getActiveScopes: () => Set<string>;
  openHelp: () => void;
  closeHelp: () => void;
  isHelpOpen: boolean;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  /** Merged registry (shell built-ins + app-supplied) for the cheat-sheet dialog. */
  shortcuts: ShortcutDefinition[];
};

const ShortcutContext = createContext<ShortcutContextValue | null>(null);

export interface ShortcutProviderProps {
  children: React.ReactNode;
  initialEnabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  /**
   * App-specific shortcut definitions. Merged with the shell's built-in
   * shortcuts (currently just `app.help`). Apps that ship without this prop
   * only get the shell built-ins.
   */
  shortcuts?: ShortcutDefinition[];
}

export const ShortcutProvider: React.FC<ShortcutProviderProps> = ({
  children,
  initialEnabled = true,
  onEnabledChange,
  shortcuts: appShortcuts,
}) => {
  const registrationsRef = useRef<Map<string, Registration[]>>(new Map());
  const scopeStackRef = useRef<string[]>([]);
  const [isHelpOpen, setHelpOpen] = useState(false);
  const [enabled, setEnabledState] = useState<boolean>(initialEnabled);

  // Store onEnabledChange in a ref so setEnabled never depends on it
  const onEnabledChangeRef = useRef(onEnabledChange);
  useEffect(() => {
    onEnabledChangeRef.current = onEnabledChange;
  }, [onEnabledChange]);

  useEffect(() => {
    setEnabledState(initialEnabled);
  }, [initialEnabled]);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    onEnabledChangeRef.current?.(next);
  }, []);

  // Merged registry: shell built-ins first, then app-supplied. App entries
  // never override shell entries because the matcher loop returns on first hit
  // and shell entries (e.g. `app.help`) are scanned first.
  const allShortcuts = useMemo<ShortcutDefinition[]>(
    () => [...SHELL_SHORTCUTS, ...(appShortcuts ?? [])],
    [appShortcuts],
  );
  const allShortcutsRef = useRef(allShortcuts);
  allShortcutsRef.current = allShortcuts;

  const register = useCallback((id: string, handler: Handler): (() => void) => {
    const def = allShortcutsRef.current.find(s => s.id === id);
    if (!def) {
      console.warn(`[shortcuts] unknown shortcut id: ${id}`);
      return () => {};
    }
    const existing = registrationsRef.current.get(id) ?? [];
    const reg: Registration = { id, handler, scope: def.scope };
    registrationsRef.current.set(id, [...existing, reg]);
    return () => {
      const current = registrationsRef.current.get(id) ?? [];
      registrationsRef.current.set(id, current.filter(r => r !== reg));
    };
  }, []);

  const pushScope = useCallback((scope: string): (() => void) => {
    scopeStackRef.current = [...scopeStackRef.current, scope];
    return () => {
      const idx = scopeStackRef.current.lastIndexOf(scope);
      if (idx >= 0) {
        scopeStackRef.current = [
          ...scopeStackRef.current.slice(0, idx),
          ...scopeStackRef.current.slice(idx + 1),
        ];
      }
    };
  }, []);

  const getActiveScopes = useCallback((): Set<string> => {
    return new Set(scopeStackRef.current);
  }, []);

  const openHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);

  const parsedBindings = useMemo(() => {
    return allShortcuts.map(def => {
      const parsed = parseBinding(def.binding);
      if (def.shiftOptional) parsed.shiftOptional = true;
      return { def, parsed };
    });
  }, [allShortcuts]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      // Never intercept browser DevTools shortcuts
      if (isBrowserReservedShortcut(event)) return;

      // Resolved lazily on the first binding that matches — this runs on
      // every keystroke, including ordinary typing, and a DOM query per
      // keypress isn't worth paying when nothing matched.
      let modalOpen: boolean | null = null;

      for (const { def, parsed } of parsedBindings) {
        if (!matchesBinding(event, parsed)) continue;

        const editable = isEditableTarget(event.target);
        if (editable && !def.allowInInput) continue;

        const unmodified = !parsed.ctrl && !parsed.meta && !parsed.alt;
        if (unmodified && (parsed.key === 'enter' || parsed.key === 'space')
            && ownsActivationKeys(event.target)) continue;

        if (def.scope && !scopeStackRef.current.includes(def.scope)) continue;

        // Nothing but the cheat-sheet toggle fires while a modal is open.
        if (modalOpen === null) modalOpen = isModalOpen();
        if (modalOpen && def.id !== 'app.help') continue;

        if (def.id === 'app.help') {
          event.preventDefault();
          setHelpOpen(prev => !prev);
          return;
        }

        const regs = registrationsRef.current.get(def.id);
        if (!regs || regs.length === 0) continue;

        const handler = regs[regs.length - 1].handler;
        event.preventDefault();
        handler(event);
        window.dispatchEvent(new CustomEvent('shortcut:invoked', {
          detail: { id: def.id, scope: def.scope ?? 'global' },
        }));
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, parsedBindings]);

  const value = useMemo<ShortcutContextValue>(() => ({
    register,
    pushScope,
    getActiveScopes,
    openHelp,
    closeHelp,
    isHelpOpen,
    enabled,
    setEnabled,
    shortcuts: allShortcuts,
  }), [register, pushScope, getActiveScopes, openHelp, closeHelp, isHelpOpen, enabled, setEnabled, allShortcuts]);

  return <ShortcutContext.Provider value={value}>{children}</ShortcutContext.Provider>;
};

export function useShortcutContext(): ShortcutContextValue {
  const ctx = useContext(ShortcutContext);
  if (!ctx) {
    throw new Error('useShortcutContext must be used inside <ShortcutProvider>');
  }
  return ctx;
}

export function useShortcut(id: string, handler: Handler): void {
  const { register } = useShortcutContext();
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);

  useEffect(() => {
    return register(id, (event) => handlerRef.current(event));
  }, [register, id]);
}

export function useShortcutScope(scope: string): void {
  const { pushScope } = useShortcutContext();
  useEffect(() => {
    return pushScope(scope);
  }, [pushScope, scope]);
}

export type { ShortcutDefinition };
